<template>
  <BaseModal v-model:is-open="isOpen">
    <!-- ヘッダー -->
    <template #title>
      {{ title }}
    </template>

    <!-- コンテンツ -->
    <template #contents>
      <!-- よく使う駅 -->
      <div v-if="showFrequentStations && frequentStations.length > 0" class="mb-6">
        <label>よく使う駅</label>
        <div class="frequent-stations mt-1">
          <v-btn
            v-for="station in frequentStations"
            :key="`${station.stationCd}-${station.stationSubCd}`"
            variant="outlined"
            color="primary"
            class="frequent-station-btn"
            data-testid="frequent-station"
            @click="selectFrequentStation(station)"
          >
            {{ station.stationNameRyaku }}
          </v-btn>
        </div>
      </div>
      <CustomButton
        variant="outlined"
        size="sm"
        class="w-100"
        @click="selectNearestStation"
      >
        <div class="d-flex align-center">
          <v-img src="~/assets/images/icons/ic_place.png" class="place-icon" />
          <span class="ms-2">現在地から最寄駅を取得</span>
        </div>
      </CustomButton>
      <div class="mt-8">
        <label for="organization_">支社名</label>
        <v-select
          id="organization_"
          v-model="selectedOrganization"
          autocomplete="off"
          placeholder="選択してください"
          :items="filteredOrganizations"
          item-title="organizationName"
          item-value="organizationCd"
          :return-object="true"
          variant="outlined"
          color="primary"
          data-testid="organization"
          @update:model-value="clearStationSelection"
        />
      </div>
      <div class="mt-3">
        <label for="station_">駅名</label>
        <v-select
          id="station_"
          v-model="selectedStation"
          autocomplete="off"
          placeholder="選択してください"
          :items="filteredStations"
          item-title="stationName"
          :item-value="getStationItemValue"
          :return-object="true"
          :disabled="!selectedOrganization"
          variant="outlined"
          color="primary"
          data-testid="station"
        />
      </div>
    </template>

    <!-- フッター -->
    <template #actionButtons>
      <CustomButton :disabled="!selectedStation" @click="handleStationSelection">
        {{ decideBtnLabel }}
      </CustomButton>
      <div v-if="!hideCancel" class="text-center mt-3">
        <CustomButton variant="text" @click="handleCancel">
          キャンセル
        </CustomButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import CustomButton from '../common/parts/CustomButton.vue';
import BaseModal from './base/BaseModal.vue';
import type { StationWithOrganization } from '~/types/common/stationWithOrganization';
import { useStationSelectModalLogic } from '~/composables/modal/useStationSelectModalLogic';
import { getStationItemValue } from '~/utils/selectItemValueUtils';

const props = withDefaults(defineProps<{
  displayId: string;
  /** モーダルの見出し */
  title: string;
  /** キャンセルボタンを隠すかどうか */
  hideCancel?: boolean;
  /** 決定ボタンのラベル */
  decideBtnLabel?: string;
  /** よく使う駅エリアを表示するかどうか */
  showFrequentStations?: boolean;
}>(), {
  decideBtnLabel: '決定',
  showFrequentStations: false,
});

// 選択した駅情報を親コンポーネントに通知するためのイベント
const emit = defineEmits<{
  stationSelected: [selectedStation: StationWithOrganization];
  canceled: [];
}>();

// showFrequentStationsは代理ログインの開始・終了で変化するため、getterで渡して都度評価させる
const {
  isOpen,
  selectedOrganization,
  selectedStation,
  filteredOrganizations,
  filteredStations,
  frequentStations,
  selectNearestStation,
  selectFrequentStation,
  clearStationSelection,
  openModal,
  closeModal,
  handleStationSelection,
  handleCancel,
  setStationSelectedCallback,
  setCanceledCallback,
} = useStationSelectModalLogic(props.displayId, () => props.showFrequentStations);

// コンポーザブルのコールバックを設定
setStationSelectedCallback((station) => {
  emit('stationSelected', station);
});

setCanceledCallback(() => {
  emit('canceled');
});

defineExpose({
  openModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
label {
  font-size: 16px;
  font-weight: bold;
}

.place-icon {
  width: 2rem;
  height: 2rem;
}

.frequent-stations {
  display: flex;
  gap: 8px;
  padding-bottom: 4px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.frequent-station-btn {
  flex: 0 0 auto;
  font-weight: bold;
  white-space: nowrap;
  background-color: white;
}
</style>
